/**
 * Returns the Romanian long name of the month corresponding to the given index.
 * @param {number} monthIndex - Index of the month (0 = January, 1 = February, etc.)
 * @returns {string} The Romanian long name of the month
 */
export const getMonthName = (monthIndex: number) => {
  return new Intl.DateTimeFormat("ro-RO", { month: "long" }).format(
    new Date(2000, monthIndex, 1)
  );
};

/**
 * Returns the Romanian short name of the day of the week corresponding to the given index.
 * @param {number} dayIndex - Index of the day (0 = Sunday, 1 = Monday, etc.)
 * @returns {string} The Romanian short name of the day.
 */
export const getDayName = (dayIndex: number) => {
  // dayIndex: 0 = Sunday, 1 = Monday, etc.
  // Create a date that falls on the desired day (using a known Sunday: 2000-01-02)
  const date = new Date(2000, 0, 2 + dayIndex);
  return new Intl.DateTimeFormat("ro-RO", { weekday: "short" }).format(date);
};

/**
 * Get the start of the week (Monday) for a given date
 * Resets time to 00:00:00 to ensure consistent date comparisons
 */
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
  d.setDate(diff);
  d.setHours(0, 0, 0, 0); // Reset time to midnight
  return d;
};

/**
 * Get array of dates for the current week
 */
export const getWeekDates = (startDate: Date): Date[] => {
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    dates.push(date);
  }
  return dates;
};

/**
 * Format date to YYYY-MM-DD
 */
export const formatDateToString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

/**
 * Check if date is today
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, new Date());
};

export interface ICalendarEvent {
  id: string;
  date: string; // YYYY-MM-DD format or YYYY-MM-DDTHH:mm format
  title?: string;
  startDate?: string; // ISO date string for spanning events
  endDate?: string; // ISO date string for spanning events
  [key: string]: any; // Allow additional properties for different event types
}

export interface ICalendarProps {
  events?: ICalendarEvent[];
  onDateSelect?: (date: Date, events: ICalendarEvent[]) => void;
  onMonthChange?: (date: Date) => void;
  initialDate?: Date;
  renderEventIndicator?: (events: ICalendarEvent[]) => React.ReactNode;
  className?: string;
  showNavigation?: boolean;
  locale?: string;
  // New props for row dimensions
  rowHeight?: string | number;
  cellHeight?: string | number;
  // Month navigation props
  showMonthNavigation?: boolean;
  currentDate?: Date; // Allow external control of current date
  onNavigateMonth?: (direction: 'prev' | 'next') => void;
  // Custom day cell renderer
  renderDayCell?: (day: number | null, events: ICalendarEvent[], isSelected: boolean) => React.ReactNode;
}

export interface IWeeklyCalendarProps {
  events?: ICalendarEvent[];
  onDateSelect?: (date: Date, events: ICalendarEvent[], timeSlot?: string) => void;
  onEventClick?: (event: ICalendarEvent) => void;
  onWeekChange?: (weekStart: Date) => void;
  initialDate?: Date;
  className?: string;
  currentDate?: Date;
  renderDayCell?: (date: Date, events: ICalendarEvent[], isSelected: boolean) => React.ReactNode;
  renderEventCard?: (event: ICalendarEvent, overlappingCount?: number) => React.ReactNode;
  timeSlots?: string[]; // Array of time slots like ["08:00", "09:00", ...]
  showTimeSlots?: boolean;
}
